<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div class="text-muted">
        Modify per-feed behavior: application method, default jobboards, NOC
        matching, auto-publish, status, notification email.
      </div>
    </div>

    <b-alert v-if="loadError" variant="danger" show>{{ loadError }}</b-alert>

    <div class="table-responsive">
      <BTable
        :items="feeds"
        :fields="fields"
        :busy="isBusy"
        hover
        show-empty
        empty-text="No client feeds found"
      >
        <template #table-busy>
          <div class="text-center my-4">
            <b-spinner class="align-middle"></b-spinner>
            <span class="ml-2">Loading...</span>
          </div>
        </template>

        <template #cell(feed)="{ item }">
          <div>
            <strong>{{ item.legacy_client }}</strong>
            <div class="text-muted small">{{ item.fetcher }}</div>
          </div>
        </template>

        <template #cell(status)="{ item }">
          <b-badge
            :variant="item.status === 'active' ? 'success' : 'secondary'"
          >
            {{ item.status === "active" ? "Active" : "Suspend" }}
          </b-badge>
        </template>

        <template #cell(application_method)="{ item }">
          <span v-if="item.application_method">{{
            applicationMethodLabel(item.application_method)
          }}</span>
          <span v-else class="text-muted">—</span>
        </template>

        <template #cell(ai_noc_matching_enabled)="{ item }">
          <i
            :class="
              item.ai_noc_matching_enabled
                ? 'fas fa-check text-success'
                : 'fas fa-times text-muted'
            "
          ></i>
        </template>

        <template #cell(auto_publish)="{ item }">
          <i
            :class="
              item.auto_publish
                ? 'fas fa-check text-success'
                : 'fas fa-times text-muted'
            "
          ></i>
        </template>

        <template #cell(default_jobboards)="{ item }">
          <span
            :title="(item.default_jobboards || []).join(', ')"
            class="d-inline-block text-truncate"
            style="max-width: 220px;"
          >
            {{ (item.default_jobboards || []).join(", ") || "—" }}
          </span>
        </template>

        <template #cell(last_fetched_at)="{ item }">
          <span
            v-if="item.last_fetched_at"
            :class="item.last_fetch_status ? '' : 'text-danger'"
          >
            {{ formatRelative(item.last_fetched_at) }}
          </span>
          <span v-else class="text-muted">never</span>
        </template>

        <template #cell(actions)="{ item }">
          <b-button size="sm" variant="primary" @click="openEdit(item)">
            <i class="fas fa-pen mr-1"></i> Edit
          </b-button>
          <b-button
            v-if="item.can_run_manually"
            size="sm"
            variant="outline-success"
            class="ml-1"
            :disabled="item.status !== 'active' || item.is_running"
            @click="runNow(item)"
          >
            <i class="fas fa-play mr-1"></i> Run now
          </b-button>
        </template>
      </BTable>
    </div>

    <ClientFeedEditModal
      :feed="editing"
      :jobboard-options="jobboardOptions"
      :available-recruiters="availableRecruiters"
      @updated="onUpdated"
      @hidden="editing = null"
    />
  </div>
</template>

<script>
import { BTable, BSpinner, BBadge, BButton, BAlert } from "bootstrap-vue";
import ClientFeedEditModal from "./ClientFeedEditModal.vue";

export default {
  name: "ClientFeedsTab",
  components: {
    BTable,
    BSpinner,
    BBadge,
    BButton,
    BAlert,
    ClientFeedEditModal
  },
  data() {
    return {
      feeds: [],
      jobboardOptions: { internal: [], external: [] },
      isBusy: true,
      loadError: null,
      editing: null,
      availableRecruiters: [],
      fields: [
        { key: "feed", label: "Feed" },
        { key: "status", label: "Status" },
        { key: "application_method", label: "Apply method" },
        { key: "ai_noc_matching_enabled", label: "NOC" },
        { key: "auto_publish", label: "Auto-pub" },
        { key: "default_jobboards", label: "Default jobboards" },
        { key: "last_fetched_at", label: "Last fetched" },
        { key: "actions", label: "" }
      ]
    };
  },
  created() {
    this.fetchFeeds();
    this.fetchJobboardOptions();
  },
  methods: {
    fetchFeeds() {
      this.isBusy = true;
      this.loadError = null;
      this.$http
        .get("/enroll_admin/client-feeds")
        .then(({ data }) => {
          this.feeds = data;
        })
        .catch(() => {
          this.loadError = "Failed to load client feeds.";
        })
        .finally(() => {
          this.isBusy = false;
        });
    },

    fetchJobboardOptions() {
      this.$http
        .get("/enroll_admin/client-feeds/jobboard-options")
        .then(({ data }) => {
          this.jobboardOptions = data;
        })
        .catch(() => {
          this.loadError = "Failed to load jobboard options.";
        });
    },

    openEdit(feed) {
      this.$http
        .get(`/enroll_admin/client-feeds/${feed.id}`)
        .then(({ data }) => {
          this.availableRecruiters = data.available_recruiters || [];
          this.editing = data;
          this.$nextTick(() => this.$bvModal.show("client-feed-edit-modal"));
        })
        .catch(() => {
          this.$bvToast.toast("Failed to load feed details.", {
            title: "Error",
            variant: "danger",
            solid: true
          });
        });
    },

    onUpdated(updated) {
      const index = this.feeds.findIndex(f => f.id === updated.id);
      if (index !== -1) {
        this.$set(this.feeds, index, updated);
      }
    },

    runNow(feed) {
      this.$http
        .put(`/enroll_admin/client-feeds/${feed.id}/run`)
        .then(() => {
          this.$bvToast.toast(`Import queued for ${feed.legacy_client}`, {
            title: "Queued",
            variant: "success",
            solid: true
          });
        })
        .catch(err => {
          const message = err?.response?.data?.error || "Failed to run feed";
          this.$bvToast.toast(message, {
            title: "Error",
            variant: "danger",
            solid: true
          });
        });
    },

    applicationMethodLabel(value) {
      return (
        {
          internal: "Internal",
          external: "External",
          external_only: "External only"
        }[value] || value
      );
    },

    formatRelative(dateStr) {
      const diffMs = Date.now() - new Date(dateStr).getTime();
      const minutes = Math.floor(diffMs / 60000);
      if (minutes < 1) return "just now";
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      return `${days}d ago`;
    }
  }
};
</script>
